/* SpawnAnything — terraced diorama terrain (pure data, worker-safe, no three.js / DOM).
   Grid of n×n cells, each cell has an integer level (terrace). Cliffs between levels block
   movement unless a ramp cell bridges them; water blocks unless a bridge spans it.
   Everything is derived from the seed (own RNG + value noise), never Math.random. */

// --- seeded helpers ---------------------------------------------------------------------
export function mulberry32(seed) {
  let s = seed >>> 0;
  return () => {
    s = (s + 0x6D2B79F5) | 0;
    let t = Math.imul(s ^ (s >>> 15), 1 | s);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}
function seedOf(v) {
  if (typeof v === 'number' && Number.isFinite(v)) return (v | 0) >>> 0;
  const s = String(v); let h = 2166136261;
  for (let i = 0; i < s.length; i++) { h ^= s.charCodeAt(i); h = Math.imul(h, 16777619); }
  return h >>> 0;
}
function hash3(x, z, s) {
  let h = (Math.imul(x | 0, 0x27d4eb2d) ^ Math.imul(z | 0, 0x165667b1) ^ Math.imul(s | 0, 0x9e3779b1)) | 0;
  h = Math.imul(h ^ (h >>> 15), 0x85ebca6b);
  h = Math.imul(h ^ (h >>> 13), 0xc2b2ae35);
  h ^= h >>> 16;
  return (h >>> 0) / 4294967296;
}
function vnoise(x, z, s) {
  const ix = Math.floor(x), iz = Math.floor(z);
  let fx = x - ix, fz = z - iz;
  fx = fx * fx * (3 - 2 * fx); fz = fz * fz * (3 - 2 * fz);
  const a = hash3(ix, iz, s), b = hash3(ix + 1, iz, s), c = hash3(ix, iz + 1, s), d = hash3(ix + 1, iz + 1, s);
  return a + (b - a) * fx + (c - a) * fz + (a - b - c + d) * fx * fz;
}
function fbm(x, z, s, oct) {
  let sum = 0, amp = 0.5, norm = 0, f = 1;
  for (let o = 0; o < oct; o++) { sum += amp * vnoise(x * f + o * 17.3, z * f - o * 9.1, s + o * 1013); norm += amp; amp *= 0.5; f *= 2.03; }
  return sum / norm;
}
/** Seeded fractal value noise in [0,1] (shared with the mesh builder for colour patches). */
export function noise2(x, z, seed = 0, oct = 3) { return fbm(x, z, seed | 0, oct); }
const smooth = (a, b, v) => { const t = Math.min(1, Math.max(0, (v - a) / (b - a))); return t * t * (3 - 2 * t); };
const clampI = (v, a, b) => v < a ? a : v > b ? b : v;

// 4-neighbour directions used for ramps: 0:+x 1:+z 2:-x 3:-z
export const DX4 = [1, 0, -1, 0], DZ4 = [0, 1, 0, -1];

// --- terrain object helpers (work on the plain object, also after structured clone) ----
/** Cell index of a world position (clamped to the grid). */
export function cellAt(t, x, z) {
  const n = t.n;
  let gx = Math.floor((x - t.origin) / t.cell), gz = Math.floor((z - t.origin) / t.cell);
  if (gx < 0) gx = 0; else if (gx >= n) gx = n - 1;
  if (gz < 0) gz = 0; else if (gz >= n) gz = n - 1;
  return gz * n + gx;
}
/** World centre of a cell → out[0]=x, out[1]=z. */
export function cellCenter(t, i, out = [0, 0]) {
  out[0] = t.origin + ((i % t.n) + 0.5) * t.cell; out[1] = t.origin + (((i / t.n) | 0) + 0.5) * t.cell;
  return out;
}
export function inBounds(t, x, z) {
  const e = t.origin + t.n * t.cell;
  return x >= t.origin && z >= t.origin && x < e && z < e;
}
export function levelAt(t, x, z) { return t.level[cellAt(t, x, z)]; }
export function isWater(t, x, z) { const i = cellAt(t, x, z); return t.water[i] > 0 && !t.bridge[i]; }
export function isWalkableCell(t, i) { return !(t.water[i] > 0 && !t.bridge[i]); }
export function isWalkable(t, x, z) { return inBounds(t, x, z) && isWalkableCell(t, cellAt(t, x, z)); }

/** Ground height in metres (smooth on ramps; water returns the water surface, bridges the deck). */
export function heightAt(t, x, z) {
  const n = t.n;
  let gx = (x - t.origin) / t.cell, gz = (z - t.origin) / t.cell;
  if (gx < 0) gx = 0; else if (gx > n - 1e-4) gx = n - 1e-4;
  if (gz < 0) gz = 0; else if (gz > n - 1e-4) gz = n - 1e-4;
  const ix = gx | 0, iz = gz | 0, i = iz * n + ix;
  if (t.water[i] && !t.bridge[i]) return t.waterLevel[i] * t.stepHeight - t.waterDrop;
  if (t.cut && t.cut[i]) return t.cutH[i]; // feature:crater (carved cell: flat top, see src/sim/features/crater.js)
  const rd = t.rampDir[i];
  if (rd >= 0) {
    const fx = gx - ix, fz = gz - iz;
    const f = rd === 0 ? fx : rd === 1 ? fz : rd === 2 ? 1 - fx : 1 - fz;
    return (t.level[i] + (t.rampK[i] + f) / t.rampLen) * t.stepHeight;
  }
  return t.level[i] * t.stepHeight;
}

// edge height of cell c on its side facing direction d (0..3), in units of stepHeight/(2*rampLen)
function edgeH2(t, c, d) {
  if (t.cut && t.cut[c]) return t.cutH[c] * 2 * t.rampLen / t.stepHeight; // feature:crater
  const R2 = t.rampLen * 2, base = t.level[c] * R2, rd = t.rampDir[c];
  if (rd < 0) return base;
  const k = t.rampK[c];
  if (d === rd) return base + 2 * (k + 1);
  if (d === ((rd + 2) & 3)) return base + 2 * k;
  return base + 2 * k + 1;
}
function stepOrth(t, a, b, d) {
  if ((t.water[a] && !t.bridge[a]) || (t.water[b] && !t.bridge[b])) return false;
  const diff = edgeH2(t, a, d) - edgeH2(t, b, (d + 2) & 3);
  return diff <= t.rampLen && diff >= -t.rampLen; // tolerance: half a step
}
/** Can a unit move from cell a to neighbouring cell b (8-neighbourhood)? Cliffs block unless ramp, water unless bridge. */
export function canStep(t, a, b) {
  const n = t.n, ax = a % n, az = (a / n) | 0, bx = b % n, bz = (b / n) | 0;
  const dx = bx - ax, dz = bz - az;
  if (bx < 0 || bz < 0 || bx >= n || bz >= n || b < 0 || b >= n * n) return false;
  if (dx < -1 || dx > 1 || dz < -1 || dz > 1 || (dx === 0 && dz === 0)) return false;
  if (dx === 0 || dz === 0) return stepOrth(t, a, b, dx === 1 ? 0 : dx === -1 ? 2 : dz === 1 ? 1 : 3);
  const o1 = a + dx, o2 = a + dz * n, dX = dx === 1 ? 0 : 2, dZ = dz === 1 ? 1 : 3;
  return stepOrth(t, a, o1, dX) && stepOrth(t, o1, b, dZ) && stepOrth(t, a, o2, dZ) && stepOrth(t, o2, b, dX);
}

/** Nearest walkable cell centre to (x,z) within maxR metres (spiral search); returns cell index or -1. */
export function nearestWalkable(t, x, z, maxR = 40) {
  const n = t.n, c0 = cellAt(t, x, z);
  if (isWalkableCell(t, c0) && inBounds(t, x, z)) return c0;
  const cx = c0 % n, cz = (c0 / n) | 0, R = Math.ceil(maxR / t.cell);
  let best = -1, bd = Infinity;
  for (let r = 1; r <= R; r++) {
    for (let dz = -r; dz <= r; dz++) for (let dx = -r; dx <= r; dx++) {
      if (Math.max(Math.abs(dx), Math.abs(dz)) !== r) continue;
      const gx = cx + dx, gz = cz + dz; if (gx < 0 || gz < 0 || gx >= n || gz >= n) continue;
      const i = gz * n + gx; if (!isWalkableCell(t, i)) continue;
      const d = dx * dx + dz * dz; if (d < bd) { bd = d; best = i; }
    }
    if (best >= 0) return best;
  }
  return -1;
}

/** Flat terrain (all level 0, no water) of n×n cells; useful as fallback and for tests. */
export function createEmptyTerrain({ n = 64, cell = 2, stepHeight = 1.2, levels = 5 } = {}) {
  const N = n * n;
  return {
    version: 1, seed: 0, n, cell, size: n * cell, origin: -n * cell / 2, stepHeight, levels, rampLen: 2,
    waterDrop: 0.32, bedDepth: 1.0,
    level: new Uint8Array(N), water: new Uint8Array(N), ramp: new Uint8Array(N), rampDir: new Int8Array(N).fill(-1),
    rampK: new Uint8Array(N), path: new Uint8Array(N), bridge: new Uint8Array(N), waterLevel: new Uint8Array(N),
    props: { trees: [], rocks: [] }, river: null,
  };
}

// --- generation -----------------------------------------------------------------------------
// Heavy loops live in module-level functions (no captured mutable state) so V8 optimises them.
const cxOf = (t, i) => t.origin + ((i % t.n) + 0.5) * t.cell;
const czOf = (t, i) => t.origin + (((i / t.n) | 0) + 0.5) * t.cell;

// rolling noise terraces outside, a gentle plain with a few one-level hillocks in the battle area
function baseLevels(t, seed, bl, bR) {
  const n = t.n, N = n * n, level = t.level, top = t.levels - 1, half = n * t.cell / 2;
  for (let i = 0; i < N; i++) {
    const x = cxOf(t, i), z = czOf(t, i), r = Math.hypot(x, z);
    const e = fbm(x / 110, z / 110, seed + 11, 4), d = fbm(x / 38 + 31, z / 38 - 17, seed + 23, 3);
    const edge = Math.max(Math.abs(x), Math.abs(z)) / half;
    const outer = bl + 0.3 + (e - 0.5) * 11 + (d - 0.5) * 4.5 + smooth(0.75, 1.0, edge) * 1.2;
    const hill = r > bR * 0.32 && fbm(x / 26 + 5, z / 26 + 9, seed + 37, 2) > 0.64 ? 1 : 0;
    const w = smooth(bR * 0.6, bR * 1.1, r);
    level[i] = clampI(Math.floor((bl + hill) * (1 - w) + outer * w + 0.5), 0, top);
  }
}
// 3×3 majority filter: turns single-cell noise into clean blocky terraces
function majority(level, n, passes) {
  const N = n * n, tmp = new Uint8Array(N), cnt = new Uint8Array(256), seen = new Uint8Array(9);
  for (let pass = 0; pass < passes; pass++) {
    for (let gz = 0; gz < n; gz++) for (let gx = 0; gx < n; gx++) {
      const i = gz * n + gx; let ns = 0;
      for (let dz = -1; dz <= 1; dz++) {
        const z = clampI(gz + dz, 0, n - 1);
        for (let dx = -1; dx <= 1; dx++) {
          const l = level[z * n + clampI(gx + dx, 0, n - 1)];
          if (cnt[l]++ === 0) seen[ns++] = l;
        }
      }
      let best = level[i], bc = cnt[best];
      if (bc < 4) for (let k = 0; k < ns; k++) { const l = seen[k]; if (cnt[l] > bc || (cnt[l] === bc && l < best)) { best = l; bc = cnt[l]; } }
      tmp[i] = best;
      for (let k = 0; k < ns; k++) cnt[seen[k]] = 0;
    }
    level.set(tmp);
  }
}

// river meanders west→east, sits one level below the battle plain and steps down in waterfalls
function makeRiver(rng, bl, top, bR) {
  const rB = Math.max(0, bl - 1), rE = Math.max(0, bl - 2);
  const side = rng() < 0.5 ? -1 : 1;
  const zc = side * bR * (0.7 + rng() * 0.1);
  const A1 = 9 + rng() * 7, f1 = 2 * Math.PI / (170 + rng() * 60), p1 = rng() * 6.283;
  const A2 = 3 + rng() * 3, f2 = 2 * Math.PI / (70 + rng() * 30), p2 = rng() * 6.283;
  const rW = Math.max(rB, Math.min(top - 1, bl + (rng() < 0.5 ? 1 : 0)));
  const drops = [];
  const xw = -bR * (0.95 + rng() * 0.3);
  if (rW - rB >= 2) drops.push({ x: xw - 40 - rng() * 25, to: rW - 1 });
  if (rW > rB) drops.push({ x: xw, to: rB });
  if (rE < rB) drops.push({ x: bR * (0.9 + rng() * 0.35), to: rE });
  return {
    zc, rW, drops, bankW: 6, terW: 12,
    z: x => zc + A1 * Math.sin(f1 * x + p1) + A2 * Math.sin(f2 * x + p2),
    dz: x => A1 * f1 * Math.cos(f1 * x + p1) + A2 * f2 * Math.cos(f2 * x + p2),
    hw: x => 6.5 + 2 * Math.sin(x * 0.013 + p2),
    level: x => { let l = rW; for (const d of drops) if (x >= d.x) l = d.to; return l; },
  };
}
// distance (m) from the river's water edge (negative inside the water)
function riverDist(rv, x, z) {
  if (!rv) return Infinity;
  const dz = rv.dz(x);
  return Math.abs(z - rv.z(x)) / Math.sqrt(1 + dz * dz) - rv.hw(x);
}
function carveRiver(t, rv) {
  const n = t.n, N = n * n, { level, water, waterLevel } = t, top = t.levels - 1;
  for (let i = 0; i < N; i++) {
    const x = cxOf(t, i), z = czOf(t, i), d = riverDist(rv, x, z), rl = rv.level(x);
    if (d < 0) { water[i] = 1; waterLevel[i] = rl; level[i] = rl; continue; }
    // stepped valley walls (bent upstream with distance → V-shaped gorge at waterfalls);
    // banks never lower than the upstream water (no leaks at waterfalls)
    const maxL = rv.level(x - d * 0.9) + 1 + Math.floor(Math.max(0, d - rv.bankW) / rv.terW);
    const minL = d < rv.bankW ? rv.level(x - 12) : 0;
    level[i] = clampI(Math.max(Math.min(level[i], maxL), minL), 0, top);
  }
}

// paths: level per 8 m segment (median of the ground below, ±1 between segments, fixed deck level near water)
function stampPaths(t, defs, rv, bl) {
  const n = t.n, N = n * n, cell = t.cell, origin = t.origin, top = t.levels - 1, half = n * cell / 2;
  const { level, water, waterLevel, path, bridge } = t;
  const best = new Float32Array(N).fill(Infinity);
  for (const pd of defs) {
    const pts = [];
    for (let s = -half - 2; s <= half + 2; s += 1) pts.push(pd.f(s));
    const SEG = 8, nseg = Math.ceil(pts.length / SEG);
    const seg = new Int16Array(nseg), fixed = new Uint8Array(nseg), ls = [];
    for (let g = 0; g < nseg; g++) {
      let deck = -1; ls.length = 0;
      for (let k = g * SEG; k < Math.min(pts.length, (g + 1) * SEG); k++) {
        const x = pts[k][0], z = pts[k][1];
        if (!inBounds(t, x, z)) continue;
        const c = cellAt(t, x, z);
        if (water[c] || riverDist(rv, x, z) < 9) deck = Math.max(deck, (rv ? rv.level(x) : waterLevel[c]) + 1);
        ls.push(level[c]);
      }
      ls.sort((a, b) => a - b);
      seg[g] = ls.length ? ls[ls.length >> 1] : bl;
      if (deck >= 0) { seg[g] = Math.min(top, deck); fixed[g] = 1; }
    }
    for (let it = 0; it < 12; it++) {
      let ch = false;
      for (let g = 1; g < nseg; g++) if (!fixed[g]) { const v = clampI(seg[g], seg[g - 1] - 1, seg[g - 1] + 1); if (v !== seg[g]) { seg[g] = v; ch = true; } }
      for (let g = nseg - 2; g >= 0; g--) if (!fixed[g]) { const v = clampI(seg[g], seg[g + 1] - 1, seg[g + 1] + 1); if (v !== seg[g]) { seg[g] = v; ch = true; } }
      if (!ch) break;
    }
    const rc = Math.ceil(pd.hw / cell) + 1;
    for (let k = 0; k < pts.length; k++) {
      const x = pts[k][0], z = pts[k][1], lv = seg[(k / SEG) | 0];
      const gx0 = Math.floor((x - origin) / cell), gz0 = Math.floor((z - origin) / cell);
      for (let dz = -rc; dz <= rc; dz++) for (let dx = -rc; dx <= rc; dx++) {
        const gx = gx0 + dx, gz = gz0 + dz; if (gx < 0 || gz < 0 || gx >= n || gz >= n) continue;
        const i = gz * n + gx, d = Math.hypot(cxOf(t, i) - x, czOf(t, i) - z);
        if (d > pd.hw || d >= best[i]) continue;
        best[i] = d; path[i] = 1; level[i] = lv;
        if (water[i]) { bridge[i] = 1; level[i] = Math.max(lv, waterLevel[i] + 1); }
      }
    }
  }
}

// connected components over orthogonal canStep moves; returns sizes, labels into comp (-1 = not walkable)
function label(t, comp, queue) {
  const n = t.n, N = n * n, sizes = [];
  comp.fill(-1);
  for (let s = 0; s < N; s++) {
    if (comp[s] >= 0 || !isWalkableCell(t, s)) continue;
    const id = sizes.length; let qh = 0, qt = 0; queue[qt++] = s; comp[s] = id;
    while (qh < qt) {
      const c = queue[qh++], gx = c % n, gz = (c / n) | 0;
      if (gx < n - 1 && comp[c + 1] < 0 && stepOrth(t, c, c + 1, 0)) { comp[c + 1] = id; queue[qt++] = c + 1; }
      if (gz < n - 1 && comp[c + n] < 0 && stepOrth(t, c, c + n, 1)) { comp[c + n] = id; queue[qt++] = c + n; }
      if (gx > 0 && comp[c - 1] < 0 && stepOrth(t, c, c - 1, 2)) { comp[c - 1] = id; queue[qt++] = c - 1; }
      if (gz > 0 && comp[c - n] < 0 && stepOrth(t, c, c - n, 3)) { comp[c - n] = id; queue[qt++] = c - n; }
    }
    sizes.push(qt);
  }
  return sizes;
}
function cellsByComp(N, comp, sizes) {
  const start = new Int32Array(sizes.length + 1);
  for (let i = 0; i < N; i++) if (comp[i] >= 0) start[comp[i] + 1]++;
  for (let k = 0; k < sizes.length; k++) start[k + 1] += start[k];
  const cur = start.slice(0, sizes.length), list = new Int32Array(start[sizes.length]);
  for (let i = 0; i < N; i++) if (comp[i] >= 0) list[cur[comp[i]]++] = i;
  return id => list.subarray(start[id], start[id + 1]);
}
function maxAdjWater(t, i) {
  const n = t.n, gx = i % n, gz = (i / n) | 0; let m = -1;
  for (let d = 0; d < 4; d++) {
    const x = gx + DX4[d], z = gz + DZ4[d]; if (x < 0 || z < 0 || x >= n || z >= n) continue;
    const j = z * n + x; if (t.water[j] && !t.bridge[j] && t.waterLevel[j] > m) m = t.waterLevel[j];
  }
  return m;
}
// merge a component into its most common neighbouring (walkable) level; returns number of changed cells
function flattenComp(t, comp, id, cells) {
  const n = t.n, votes = new Map();
  for (const c of cells) {
    const gx = c % n, gz = (c / n) | 0;
    for (let d = 0; d < 4; d++) {
      const x = gx + DX4[d], z = gz + DZ4[d]; if (x < 0 || z < 0 || x >= n || z >= n) continue;
      const j = z * n + x; if (comp[j] === id || !isWalkableCell(t, j)) continue;
      // a ramp neighbour counts as the level its side edge is closest to
      const l = t.level[j] + (t.rampDir[j] >= 0 ? Math.round((t.rampK[j] + 0.5) / t.rampLen) : 0);
      votes.set(l, (votes.get(l) || 0) + 1);
    }
  }
  let bl = -1, bc = -1;
  for (const [l, v] of votes) if (v > bc || (v === bc && l < bl)) { bl = l; bc = v; }
  if (bl < 0) return 0;
  let ch = 0;
  for (const c of cells) {
    if (t.bridge[c]) continue;
    const l = Math.min(t.levels - 1, Math.max(bl, maxAdjWater(t, c)));
    if (l !== t.level[c] || t.rampDir[c] >= 0) ch++;
    t.level[c] = l; t.rampDir[c] = -1; t.ramp[c] = 0; t.rampK[c] = 0;
  }
  return ch;
}

const isFlatLand = (t, i) => !t.water[i] && t.rampDir[i] < 0;
// one ramp lane: low cells a2,a (level L) rising toward b,b2 (level L+1) in direction d
function lane(t, a, d) {
  const n = t.n, gx = a % n, gz = (a / n) | 0, dx = DX4[d], dz = DZ4[d];
  const x2 = gx - dx, z2 = gz - dz, xb2 = gx + 2 * dx, zb2 = gz + 2 * dz;
  if (x2 < 0 || z2 < 0 || x2 >= n || z2 >= n || xb2 < 0 || zb2 < 0 || xb2 >= n || zb2 >= n) return false;
  const a2 = z2 * n + x2, b = a + dx + dz * n, b2 = zb2 * n + xb2, L = t.level[a];
  if (!isFlatLand(t, a) || !isFlatLand(t, a2) || !isFlatLand(t, b) || !isFlatLand(t, b2)) return false;
  if (t.level[a2] !== L || t.level[b] !== L + 1 || t.level[b2] !== L + 1) return false;
  t.rampDir[a2] = d; t.rampK[a2] = 0; t.ramp[a2] = 1;
  t.rampDir[a] = d; t.rampK[a] = 1; t.ramp[a] = 1;
  return true;
}
// ramp of up to 1+2*halfW lanes centred on a
function placeRamp(t, a, d, halfW) {
  if (!lane(t, a, d)) return false;
  const n = t.n, px = DZ4[d] !== 0 ? 1 : 0, pz = DX4[d] !== 0 ? 1 : 0, gx = a % n, gz = (a / n) | 0;
  for (let s = -1; s <= 1; s += 2) for (let k = 1; k <= halfW; k++) {
    const x = gx + px * s * k, z = gz + pz * s * k; if (x < 0 || z < 0 || x >= n || z >= n) break;
    if (!lane(t, z * n + x, d)) break;
  }
  return true;
}
// ramp candidates (packed a*4+d, a = low cell), best first: path crossings, then near the centre
// mode 0: all, 1: both cells on a path, 2: cells in different components
function candidates(t, seed, mode, comp) {
  const n = t.n, N = n * n, ids = [], sc = [], level = t.level, path = t.path;
  for (let a = 0; a < N; a++) {
    if (!isFlatLand(t, a)) continue;
    const gx = a % n, gz = (a / n) | 0, L = level[a];
    for (let d = 0; d < 4; d++) {
      const x = gx + DX4[d], z = gz + DZ4[d]; if (x < 0 || z < 0 || x >= n || z >= n) continue;
      const b = z * n + x; if (level[b] !== L + 1 || !isFlatLand(t, b)) continue;
      if (mode === 1 && !(path[a] && path[b])) continue;
      if (mode === 2 && comp[a] === comp[b]) continue;
      ids.push(a * 4 + d);
      sc.push(Math.hypot(cxOf(t, a), czOf(t, a)) - (path[a] || path[b] ? 300 : 0) + hash3(a, d, seed) * 6);
    }
  }
  const ord = Int32Array.from(ids.keys()).sort((p, q) => sc[p] - sc[q] || ids[p] - ids[q]);
  const out = new Int32Array(ord.length);
  for (let k = 0; k < ord.length; k++) out[k] = ids[ord[k]];
  return out;
}
function buildRamps(t, seed, bR) {
  const n = t.n, N = n * n, comp = new Int32Array(N), queue = new Int32Array(N), rampPts = [];
  // tiny isolated terraces / pits first
  for (let it = 0; it < 4; it++) {
    const sizes = label(t, comp, queue), get = cellsByComp(N, comp, sizes); let ch = 0;
    for (let id = 0; id < sizes.length; id++) if (sizes[id] < 24) ch += flattenComp(t, comp, id, get(id));
    if (!ch) break;
  }
  // where paths change level
  for (const id of candidates(t, seed, 1, comp)) { const a = id >> 2; if (lane(t, a, id & 3)) rampPts.push(cxOf(t, a), czOf(t, a)); }
  // connect all components (Kruskal over candidates); merge leftover pockets into their surroundings
  for (let it = 0; it < 8; it++) {
    let sizes = label(t, comp, queue);
    const uf = Int32Array.from(sizes.keys());
    const find = x => { while (uf[x] !== x) { uf[x] = uf[uf[x]]; x = uf[x]; } return x; };
    for (const id of candidates(t, seed, 2, comp)) {
      const a = id >> 2, d = id & 3, b = a + DX4[d] + DZ4[d] * n;
      const ra = find(comp[a]), rb = find(comp[b]);
      if (ra === rb) continue;
      if (placeRamp(t, a, d, 1)) { uf[ra] = rb; rampPts.push(cxOf(t, a), czOf(t, a)); }
    }
    sizes = label(t, comp, queue);
    let main = comp[cellAt(t, 0, 0)];
    if (main < 0) { main = 0; for (let k = 1; k < sizes.length; k++) if (sizes[k] > sizes[main]) main = k; }
    const get = cellsByComp(N, comp, sizes); let ch = 0;
    // only modest pockets are merged; big regions cut off by water (no bridge) stay as they are
    for (let id = 0; id < sizes.length; id++) if (id !== main && sizes[id] < N * 0.04) ch += flattenComp(t, comp, id, get(id));
    if (!ch) break;
  }
  // extra ramps for short detours: dense in the battle area, sparse outside
  const B = 16, gb = Math.ceil(n * t.cell / B) + 1, buckets = new Map(), origin = t.origin;
  const add = (x, z) => { const k = Math.floor((x - origin) / B) * gb + Math.floor((z - origin) / B); let l = buckets.get(k); if (!l) buckets.set(k, l = []); l.push(x, z); };
  const near = (x, z, R) => {
    const r = Math.ceil(R / B), bx = Math.floor((x - origin) / B), bz = Math.floor((z - origin) / B);
    for (let i = -r; i <= r; i++) for (let j = -r; j <= r; j++) {
      const l = buckets.get((bx + i) * gb + bz + j); if (!l) continue;
      for (let k = 0; k < l.length; k += 2) if ((l[k] - x) ** 2 + (l[k + 1] - z) ** 2 < R * R) return true;
    }
    return false;
  };
  for (let k = 0; k < rampPts.length; k += 2) add(rampPts[k], rampPts[k + 1]);
  for (const id of candidates(t, seed, 0, comp)) {
    const a = id >> 2, x = cxOf(t, a), z = czOf(t, a), R = Math.hypot(x, z) < bR ? 22 : 46;
    if (near(x, z, R)) continue;
    if (placeRamp(t, a, id & 3, 1)) add(x, z);
  }
}
function markFalls(t) {
  const n = t.n, N = n * n, { water, waterLevel } = t;
  for (let i = 0; i < N; i++) {
    if (!water[i]) continue;
    const gx = i % n, gz = (i / n) | 0;
    for (let d = 0; d < 4; d++) {
      const x = gx + DX4[d], z = gz + DZ4[d]; if (x < 0 || z < 0 || x >= n || z >= n) continue;
      const j = z * n + x; if (water[j] && waterLevel[j] < waterLevel[i]) { water[i] = 2; break; }
    }
  }
}
// tree/rock positions: forests in noise clusters (sparse inside the battle area), rocks at cliff bases and banks
function placeProps(t, seed, bR, maxTrees, maxRocks) {
  const n = t.n, N = n * n, cell = t.cell, { level, water, path, rampDir } = t, trees = [], rocks = [];
  const r2 = v => Math.round(v * 100) / 100;
  for (let i = 0; i < N; i++) {
    const gx = i % n, gz = (i / n) | 0;
    if (gx < 1 || gz < 1 || gx >= n - 1 || gz >= n - 1 || !isFlatLand(t, i) || path[i]) continue;
    let nearPath = false, nearWater = false, cliffBase = false;
    for (let dz = -1; dz <= 1; dz++) for (let dx = -1; dx <= 1; dx++) {
      const j = (gz + dz) * n + gx + dx;
      if (path[j] || rampDir[j] >= 0) nearPath = true;
      if (water[j]) nearWater = true;
      if (level[j] > level[i]) cliffBase = true;
    }
    const x = cxOf(t, i), z = czOf(t, i), r = Math.hypot(x, z);
    const jx = (hash3(i, 1, seed + 5) - 0.5) * cell * 0.7, jz = (hash3(i, 2, seed + 5) - 0.5) * cell * 0.7;
    if (!nearPath && !nearWater) {
      const f = fbm(x / 60, z / 60, seed + 71, 3), inner = r < bR * 0.8 ? 0.06 : r < bR ? 0.3 : 1;
      if (hash3(i, 3, seed + 7) < smooth(0.5, 0.72, f) * 0.32 * inner + 0.003) {
        const kind = clampI(Math.floor(fbm(x / 90 + 50, z / 90, seed + 73, 2) * 3.2 - 0.1), 0, 2);
        trees.push([r2(x + jx), r2(z + jz), r2(0.8 + hash3(i, 4, seed) * 0.6), kind]);
        continue;
      }
    }
    if (!nearPath && hash3(i, 6, seed + 9) < (cliffBase ? 0.05 : nearWater ? 0.035 : 0.004)) rocks.push([r2(x + jx), r2(z + jz), r2(0.6 + hash3(i, 7, seed) * 0.8)]);
  }
  const thin = (a, m) => a.length <= m ? a : Array.from({ length: m }, (_, k) => a[Math.floor(k * a.length / m)]);
  return { trees: thin(trees, maxTrees), rocks: thin(rocks, maxRocks) };
}

/**
 * Generate a terraced diorama map. All arrays are n×n, index = gz*n + gx, cell centre
 * x = origin + (gx+0.5)*cell. Levels are integers; ground height = level*stepHeight.
 */
export function generateTerrain(opts = {}) {
  const o = {
    seed: 1, size: 460, cell: 2, stepHeight: 1.2, levels: 5, river: true, paths: true, battleRadius: 120,
    maxTrees: 900, maxRocks: 320, ...opts,
  };
  const seed = seedOf(o.seed);
  const rng = mulberry32(seed ^ 0x5bd1e995);
  const n = Math.max(8, Math.round(o.size / o.cell));
  const t = createEmptyTerrain({ n, cell: o.cell, stepHeight: o.stepHeight, levels: Math.max(2, Math.min(250, o.levels | 0)) });
  t.seed = seed;
  const top = t.levels - 1, bR = o.battleRadius;
  const bl = clampI(Math.round(top / 2), Math.min(1, top), top); // battle plain level (2 for 5 levels)

  baseLevels(t, seed, bl, bR);
  majority(t.level, n, 2);
  const rv = o.river ? makeRiver(rng, bl, top, bR) : null;
  if (rv) carveRiver(t, rv);
  const defs = [], bridgeX = [];
  if (o.paths) {
    // E-W road through the battle area, two N-S roads crossing the river on bridges
    const pz = (rng() - 0.5) * 14, aE = 5 + rng() * 6, fE = 2 * Math.PI / (160 + rng() * 80), pE = rng() * 6.283;
    defs.push({ hw: 4.2, f: s => [s, pz + aE * Math.sin(fE * s + pE)] });
    for (const sg of [-1, 1]) {
      const xb = sg * (26 + rng() * 34), aN = 4 + rng() * 5, fN = 2 * Math.PI / (120 + rng() * 60), pN = rng() * 6.283;
      bridgeX.push(xb);
      defs.push({ hw: 3.4, f: s => [xb + aN * Math.sin(fN * s + pN), s] });
    }
    stampPaths(t, defs, rv, bl);
  }
  buildRamps(t, seed, bR);
  markFalls(t);
  t.props = placeProps(t, seed, bR, o.maxTrees, o.maxRocks);
  t.battleLevel = bl;
  t.river = rv ? { zc: rv.zc, drops: rv.drops.map(d => ({ x: d.x, to: d.to })), sourceLevel: rv.rW, bridgeX } : null;
  return t;
}
